import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';

/**
 * Tracks viewport measurements and exposes responsive signals that can be shared across components.
 */
@Injectable({
    providedIn: 'root',
})
export class ViewportService {
    private static readonly DefaultMobileBreakpoint = 768;

    private readonly destroyRef = inject(DestroyRef);

    private readonly mobileBreakpoint = signal(ViewportService.DefaultMobileBreakpoint);
    private readonly viewportWidth = signal(this.readViewportWidth());
    private readonly viewportHeight = signal(this.readViewportHeight());

    readonly isActuallyMobileBrowser = signal(false);
    readonly width = computed(() => this.viewportWidth());
    readonly height = computed(() => this.viewportHeight());
    readonly isMobileDimension = computed(() => this.isActuallyMobileBrowser() || this.width() <= this.mobileBreakpoint());

    constructor() {
        if (typeof window === 'undefined') {
            return;
        }

        const handleResize = () => {
            this.viewportWidth.set(this.readViewportWidth());
            this.viewportHeight.set(this.readViewportHeight());
        };

        window.addEventListener('resize', handleResize, { passive: true });

        this.destroyRef.onDestroy(() => {
            window.removeEventListener('resize', handleResize);
        });

        this.determineMobileBrowserFromTouchpoints();
    }

    private determineMobileBrowserFromTouchpoints() {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            this.isActuallyMobileBrowser.set(false);

            return;
        }

        const hasTouch = navigator.maxTouchPoints > 1;
        const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        this.isActuallyMobileBrowser.set(hasTouch && isMobileUA);
    }

    private readViewportWidth(): number {
        if (typeof window === 'undefined') {
            return this.mobileBreakpoint();
        }

        return window.innerWidth;
    }

    private readViewportHeight(): number {
        if (typeof window === 'undefined') {
            return 0;
        }

        return window.innerHeight;
    }
}
